import React, { useEffect, useRef, useState } from 'react';
import { Image, View } from 'react-native';

import { map as _map, isEmpty as _isEmpty, isNil as _isNil } from 'lodash';
import PropTypes from 'prop-types';

import {
  ErrorScreen,
  FilterBar,
  UsageItem,
  VFText,
} from '@vfgroup-oneplatform/foundation/Components';
import { Colors, LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';
import ConsumerAddOnsCard from '@vfgroup-oneplatform/framework/AddOns/Components/AddOnsCard';

import useKeyboardInfo from '../../Common/Hooks/KeyboardAvoiding/KeyboardAvoiding.Hooks';
import SearchBar from '../../Common/UI/SearchBar/SearchBar';
import { getTotalLicenceCount } from '../../Services/BusinessOverview/Licences/Licences';
import { PRODUCT_STATUS } from '../../Services/Enums';
import { getThemeImages, Images } from '../../Themes';
import SohoFrameworkTestKeys from '../../Utils/SohoFrameworkTestKeys';
import { useProductsCategoriesFilter, useMatchedFilterSearchedProducts } from '../Utils/Hooks';

import LicencesLoading from './Components/LicencesLoading/LicencesLoading';
import styles from './Licences.Styles';

function renderCardBody(item, isCurrencyFirst) {
  const price = isCurrencyFirst
    ? `${item?.price?.currency}${item?.price?.value}`
    : `${item?.price?.value}${item?.price?.currency}`;
  const { unitOfMeasure = '' } = item?.price;
  const licencesCount = getTotalLicenceCount([item]);
  const activeLicencesCount = getTotalLicenceCount([item], PRODUCT_STATUS.ACTIVE);

  return (
    <>
      <VFText
        i18nKey={`${price} ${unitOfMeasure}`}
        style={styles.cardLicencesPricePerUserTextStyle}
      />
      <VFText
        i18nKey={'soho_licences_used_count_format'}
        placeHolders={[`${activeLicencesCount}/${licencesCount}`]}
        style={styles.cardLicencesCountTextStyle}
      />
    </>
  );
}

function renderPMCardBody(item, isCurrencyFirst, withPriceVat) {
  const price = isCurrencyFirst
    ? `${item?.price?.currency}${item?.price?.value}`
    : `${item?.price?.value}${item?.price?.currency}`;
  return (
    <>
      <View style={styles.pmCardBodyContainer}>
        <VFText i18nKey={`${price} `} style={styles.profileManagementCardTextStyle} />
        {withPriceVat && (
          <VFText
            i18nKey="soho_profile_management_licences_include_vat"
            style={styles.profileManagementCardTextStyle}
          />
        )}
      </View>
    </>
  );
}

const renderLicencesUsageTitle = () => {
  return (
    <VFText
      testKey={SohoFrameworkTestKeys.LicencesUsageTitleText}
      i18nKey={'soho_licences_usage'}
      type="primary"
      style={[styles.titleTextStyle, styles.licenceUsageStyle]}
    />
  );
};

const renderLicencesUsage = ({ applications, theme }) => {
  const allActiveLicencesCount = getTotalLicenceCount(applications, PRODUCT_STATUS.ACTIVE);
  const allLicencesCount = getTotalLicenceCount(applications);
  const valuePercentage = !_isEmpty(applications)
    ? parseInt((allActiveLicencesCount / allLicencesCount) * 100, 10)
    : 0;
  return (
    <>
      {renderLicencesUsageTitle()}
      <VFText
        i18nKey={'soho_licences_and_applications_count'}
        placeHolders={[
          getTotalLicenceCount(applications, PRODUCT_STATUS.ACTIVE),
          applications?.length,
        ]}
        style={styles.descriptionTextStyle}
      />
      <UsageItem
        percentageIndicatorSecondaryColor={Colors.secondaryRed}
        containerStyle={styles.usageContainerStyle(theme)}
        usageItemLabel={'soho_licences_statistics_card_title'}
        icon={'ic_licences'}
        usageValuesTextStyle={styles.usageValuesTextStyle}
        usageValuesPlaceholders={[allActiveLicencesCount, allLicencesCount]}
        usageValuesText={'soho_licences_usage_card_consumption'}
        usagePercentageValue={`${valuePercentage}`}
        images={Images}
        iconSize={20}
        usageIconViewStyle={styles.usageIconViewStyle}
      />
    </>
  );
};

const onLayout = ({
  scrollToOffset,
  searchObj,
  keyboardHeight,
  isPageBannerVisible,
  isScrolled,
  setIsScrolled,
}) => {
  if (keyboardHeight !== 0 && !isScrolled) {
    const isEmptySearchedData =
        searchObj.searchWord.length != 0 && searchObj.searchedData.length == 0,
      maxScrollHeight = 10000,
      scrollHeight = isPageBannerVisible ? 400 : 200;
    setIsScrolled(true);
    scrollToOffset(isEmptySearchedData ? maxScrollHeight : scrollHeight, true);
  }
};

const onChangeText = (selectedCategories, setSelectedCategories) => {
  if (selectedCategories.length != 0) {
    setSelectedCategories([]);
  }
};

const renderSearchBar = ({
  applications,
  selectedCategories,
  setSelectedCategories,
  setSearchObj,
  searchBarRef,
}) => {
  return (
    <SearchBar
      style={styles.searchStyle}
      ref={searchBarRef}
      filterKeyword="name"
      data={[applications]}
      onFilterData={(searchWord, searchedData) =>
        setSearchObj({ searchWord, searchedData: searchedData[0] })
      }
      onChangeText={() => onChangeText(selectedCategories, setSelectedCategories)}
    />
  );
};

const renderEmptySearchResult = ({ searchWord }) => {
  return (
    <View style={styles.emptyScenarioTextContainer}>
      <VFText
        i18nKey={'soho_search_empty_view_message'}
        placeHolders={[searchWord]}
        style={styles.emptyScenarioTextStyle}
      />
    </View>
  );
};

const renderLicencesList = ({
  matchedApplications,
  selectedCategories,
  categories,
  onSelectedCategoriesChange,
  setSelectedCategories,
  theme,
  images,
  applications,
  setSearchObj,
  searchBarRef,
  isCurrencyFirst,
  withFilterAndSearchBars,
  isProfileManagement,
  withPriceVat,
  licencesTitle,
  withLicenceUsage,
}) => {
  const _handleOnSelectedCategoriesChange = type => {
    searchBarRef?.current?.blurSearchBar();
    onSelectedCategoriesChange(type);
  };

  return (
    <>
      <VFText
        i18nKey={licencesTitle}
        type="primary"
        style={[styles.titleTextStyle, styles.yourLicencesStyle(withLicenceUsage)]}
      />
      {isProfileManagement && (
        <VFText
          i18nKey={'soho_profile_management_active_current_licences'}
          style={styles.pmDescriptionTextStyle}
        />
      )}

      {withFilterAndSearchBars &&
        renderSearchBar({
          applications,
          selectedCategories,
          setSelectedCategories,
          setSearchObj,
          searchBarRef,
        })}

      {withFilterAndSearchBars && (
        <>
          <FilterBar
            filterData={categories}
            selectedCategories={selectedCategories}
            setSelectedCategories={_handleOnSelectedCategoriesChange}
            style={styles.filterBarStyle}
          />
          <View style={styles.dividerContainer}>
            <View style={styles.divider(theme)} />
          </View>
        </>
      )}

      {_map(matchedApplications, (application, index) => {
        const consumerAddOnsCardProps = {
          key: application?.id || index,
          item: application,
          style: styles.cardStyle,
          leftIconStyle: styles.leftIconStyle,
          rightIconSize: 32,
          rightIconStyle: styles.rightIconStyle,
          renderCardBody: item =>
            !isProfileManagement
              ? renderCardBody(item, isCurrencyFirst)
              : renderPMCardBody(item, isCurrencyFirst, withPriceVat),
          images: images,
          ...(isProfileManagement && {
            topTextStyle: styles.cardTopTextStyle,
            leftIconSize: 32,
            leftIconStyle: undefined,
            rightIconStyle: undefined,
            style: { ...styles.cardStyle, ...styles.cardPadding },
          }),
        };

        return <ConsumerAddOnsCard {...consumerAddOnsCardProps} />;
      })}
    </>
  );
};

function Licences({
  applications,
  isLoading,
  error,
  onTryAgainPress,
  theme,
  withTray,
  images,
  scrollToOffset,
  isCurrencyFirst,
  withLicenceUsage,
  withFilterAndSearchBars,
  licencesTitle,
  isProfileManagement,
  withPriceVat,
  isPageBannerVisible,
  ...props
}) {
  const [searchObj, setSearchObj] = useState({ searchWord: '', searchedData: [] });
  const [isScrolled, setIsScrolled] = useState(false);
  const searchBarRef = useRef(null);
  const {
    products: applicationsList,
    selectedCategories,
    categories,
    onSelectedCategoriesChange,
    setSelectedCategories,
  } = useProductsCategoriesFilter(applications, undefined, true);
  const { matchedProducts: matchedApplications } = useMatchedFilterSearchedProducts(
    applicationsList,
    searchObj,
  );
  const { keyboardHeight } = useKeyboardInfo();

  const renderEmptyLicencesUsage = () => {
    const sohoImages = getThemeImages();
    return (
      <>
        {renderLicencesUsageTitle()}
        <View style={styles.emptyLicencesContainer}>
          <Image
            source={sohoImages.licences_empty}
            style={styles.emptyLicencesImage}
            testID={SohoFrameworkTestKeys.LicencesEmptyImage}
          />
          <VFText i18nKey="soho_licences_empty_description" style={styles.emptyCaseTitle} />
          <VFText
            i18nKey="soho_licences_empty_small_description"
            style={styles.emptyCaseSubtitle}
          />
        </View>
      </>
    );
  };

  const renderContent = () => {
    return !_isEmpty(applications) ? (
      <>
        {withLicenceUsage && renderLicencesUsage({ applications, theme })}
        {renderLicencesList({
          matchedApplications,
          selectedCategories,
          setSelectedCategories,
          categories,
          onSelectedCategoriesChange,
          theme,
          images,
          applications,
          setSearchObj,
          searchBarRef,
          isCurrencyFirst,
          withFilterAndSearchBars,
          licencesTitle,
          isProfileManagement,
          withPriceVat,
          withLicenceUsage,
        })}
        {matchedApplications?.length === 0 && renderEmptySearchResult(searchObj)}
      </>
    ) : (
      renderEmptyLicencesUsage()
    );
  };

  const renderLoading = () => {
    return <LicencesLoading testID={SohoFrameworkTestKeys.LicencesLoading} />;
  };

  const renderError = () => {
    return (
      <>
        {renderLicencesUsageTitle()}
        <ErrorScreen
          iconStyle={styles.errorIconStyle}
          text={error?.message}
          {...error}
          onPress={onTryAgainPress}
          textButton={'soho_licences_failure_error_try_again'}
          testID={SohoFrameworkTestKeys.LicencesError}
        />
      </>
    );
  };

  useEffect(() => {
    if (keyboardHeight === 0) {
      setIsScrolled(false);
    }
  }, [keyboardHeight]);

  return (
    <View
      onLayout={() =>
        onLayout({
          scrollToOffset,
          searchObj,
          keyboardHeight,
          isPageBannerVisible,
          isScrolled,
          setIsScrolled,
        })
      }
      style={styles.containerStyle(withTray, keyboardHeight)}
    >
      {isLoading ? renderLoading() : !_isNil(error) ? renderError() : renderContent()}
    </View>
  );
}

Licences.defaultProps = {
  theme: LightThemeColors,
  isCurrencyFirst: false,
  withFilterAndSearchBars: true,
  withLicenceUsage: true,
  isProfileManagement: false,
  withPriceVat: false,
  isPageBannerVisible: false,
  licencesTitle: 'soho_licences_your_licences',
};

Licences.propTypes = {
  applications: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  onTryAgainPress: PropTypes.func,
  theme: PropTypes.object,
  withTray: PropTypes.bool,
  images: PropTypes.object,
  scrollToOffset: PropTypes.func,
  isCurrencyFirst: PropTypes.bool,
  withLicenceUsage: PropTypes.bool,
  withFilterAndSearchBars: PropTypes.bool,
  licencesTitle: PropTypes.string,
  isProfileManagement: PropTypes.bool,
  withPriceVat: PropTypes.bool,
  isPageBannerVisible: PropTypes.bool,
};

export default Licences;
